import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from './Container';
import Eyebrow from './Eyebrow';
import Button from './Button';

// Degradé de legibilidad sobre la imagen: oscurece la izquierda (donde va el
// texto) y deja respirar la derecha. Mismo tratamiento que el hero del inicio.
const LEGIBILITY_GRADIENT =
  'linear-gradient(90deg,rgba(11,18,36,0.94) 0%,rgba(13,21,42,0.82) 46%,rgba(14,23,46,0.55) 100%)';

const DIAGONAL_TEXTURE =
  'repeating-linear-gradient(120deg,#16233f,#16233f 22px,#1a2947 22px,#1a2947 44px)';

/**
 * Hero interior de página. Fondo navy con la portada de la sección (o, si no
 * hay `image`, una textura de líneas diagonales), eyebrow, h1, subtítulo y
 * hasta dos acciones.
 *
 * Por defecto replica el hero del inicio (alto de viewport + centrado
 * vertical) para que todas las portadas arranquen a la misma altura.
 * `compact` (perfiles de `/equipo/[slug]`) usa una caja más baja; la imagen
 * de fondo (si la hay) funciona igual.
 */
export default function PageHero({
  eyebrow,
  title,
  subtitle,
  image,
  actions = [],
  compact = false,
}) {
  const hasImage = Boolean(image);

  return (
    <Box
      component="header"
      data-nav-theme="dark"
      sx={{
        position: 'relative',
        ...(compact
          ? { pt: 'clamp(140px, 20vw, 190px)', pb: 'clamp(72px, 12vw, 120px)' }
          : { minHeight: '100vh', display: 'flex', alignItems: 'center' }),
        bgcolor: 'navy.main',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          ...(hasImage
            ? {
                backgroundImage: `${LEGIBILITY_GRADIENT}, url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { background: DIAGONAL_TEXTURE, opacity: 0.6 }),
        },
      }}
    >
      <Container
        sx={{
          position: 'relative',
          zIndex: 1,
          ...(compact
            ? {}
            : {
                pt: { xs: '110px', md: '150px' },
                pb: { xs: '90px', md: '110px' },
              }),
        }}
      >
        <Box
          sx={{
            maxWidth: compact ? 760 : 920,
            // Reserva un alto parecido al del hero del inicio para que el
            // eyebrow arranque a la misma altura aunque el texto sea más corto.
            ...(compact ? {} : { minHeight: { md: 380 } }),
            animation: 'gtFadeUp .9s ease both',
          }}
        >
          {eyebrow && (
            <Box sx={{ mb: '28px' }}>
              <Eyebrow tone="dark">{eyebrow}</Eyebrow>
            </Box>
          )}
          <Typography
            variant="h1"
            component="h1"
            // Más chico que el h1 base (60px en desktop) para que el título
            // entre en dos líneas, no tres, con el largo estándar (~50 car.).
            sx={{
              color: 'text.heading',
              mb: '26px',
              ...(compact ? {} : { fontSize: { xs: '42px', md: '60px' } }),
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: 'clamp(16px, 1.5vw, 19px)',
                lineHeight: 1.75,
                color: 'text.onDark',
                fontWeight: 300,
                maxWidth: 600,
                mb: actions.length ? '38px' : 0,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {actions.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                '& > a': { width: { xs: '100%', sm: 'auto' } },
              }}
            >
              {actions.map((a, i) => (
                <Button
                  key={i}
                  href={a.href}
                  external={a.external}
                  variant={i === 0 ? 'solid' : 'ghost'}
                  {...(a.external && { target: '_blank' })}
                >
                  {a.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
